import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './Nav.css';

const Nav = () => {
  const { t, language, setLanguage } = useLanguage();
  const [servicesOpen, setServicesOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const rafRef = useRef(null);

  const updateNavVisibility = useCallback(() => {
    const currentScrollY = window.scrollY;
    
    if (currentScrollY < 100) {
      setIsVisible(true);
    } else if (currentScrollY > lastScrollY.current) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    
    lastScrollY.current = currentScrollY;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateNavVisibility);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [updateNavVisibility]);

  const toggleLanguage = () => {
    setLanguage(language === 'de' ? 'en' : 'de');
  };

  return (
    <>
    <div className={`nav-fox__floating-icon ${!isVisible && lastScrollY.current > 100 ? 'nav-fox__floating-icon--visible' : ''}`}>
      <Link to="/">
        <img 
          src="/img/fox_icon.png" 
          alt="Fox Performance"
          width={40}
          height={40}
          loading="eager"
        />
      </Link>
    </div>
    
    <nav className={`nav-fox ${!isVisible ? 'nav-fox--hidden' : ''}`}>
      <div className="nav-fox__container">
        <Link to="/" className="nav-fox__logo-link">
          <img 
            src="/img/fox_performance_logo.png" 
            alt="Fox Performance" 
            className="nav-fox__logo"
            width={180}
            height={40}
            loading="eager"
            fetchpriority="high"
          />
        </Link>
        <div className="nav-fox__right">
          <div className="nav-fox__links">
            <div 
              className="nav-fox__dropdown"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button className="nav-fox__link nav-fox__link--dropdown">
                {t.nav.services}
                <svg 
                  className={`nav-fox__dropdown-icon ${servicesOpen ? 'nav-fox__dropdown-icon--open' : ''}`}
                  width="10" 
                  height="6" 
                  viewBox="0 0 10 6" 
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {servicesOpen && (
                <div className="nav-fox__dropdown-menu">
                  <a href="#ppc" className="nav-fox__dropdown-item">{t.nav.amazonPPC}</a>
                  <a href="#produktbilder" className="nav-fox__dropdown-item">{t.nav.produktbilder}</a>
                  <a href="#seo" className="nav-fox__dropdown-item">{t.nav.amazonSEO}</a>
                  <Link to="/portfolio" className="nav-fox__dropdown-item">{t.nav.designPortfolio}</Link>
                </div>
              )}
            </div>
            <a href="#testimonials" className="nav-fox__link">{t.nav.customerSuccess}</a>
          </div>
          <button
            className="nav-fox__lang-toggle"
            onClick={toggleLanguage}
            aria-label={`Switch to ${language === 'de' ? 'English' : 'Deutsch'}`}
          >
            {language === 'de' ? 'EN' : 'DE'}
          </button>
          <a 
            href="https://www.linkedin.com/company/fox-performance" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="nav-fox__linkedin-link"
            aria-label="LinkedIn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <a
            href="https://meetings-eu1.hubspot.com/michael-wenger?uuid=9cf1d252-81d9-4d0a-ac62-21972be61c70"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-fox__cta-button"
          >
            {t.nav.contact}
          </a>
        </div>
      </div>
    </nav>
    </>
  );
};

export default Nav;
